import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Gift, Copy, Check, Sparkles, Users, Share2 } from "lucide-react";

export default function Refer() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [claimCode, setClaimCode] = useState("");
  const [claimMsg, setClaimMsg] = useState("");

  const load = () => axios.get(`${API}/referrals/me`).then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const shareUrl = data ? `${window.location.origin}/?ref=${data.code}` : "";

  const copy = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const share = async () => {
    if (navigator.share && shareUrl) {
      try {
        await navigator.share({
          title: "Join me on PetBond",
          text: "I'm using PetBond for everything pet — vets, store, adoption & more. Join me!",
          url: shareUrl,
        });
        return;
      } catch {}
    }
    copy();
  };

  const claim = async (e) => {
    e.preventDefault();
    setClaimMsg("");
    try {
      const r = await axios.post(`${API}/referrals/claim`, { code: claimCode.trim().toUpperCase() });
      if (r.data.ok) {
        setClaimMsg(`Yay! ${r.data.inviter_name} invited you — both of you earned 1 month free 🎉`);
        setClaimCode("");
        await load();
      } else {
        setClaimMsg("You already claimed a referral.");
      }
    } catch (e) {
      setClaimMsg(e.response?.data?.detail || "Could not apply that code.");
    }
  };

  if (!data) return <div className="text-ink-soft">Loading…</div>;

  const reward_months = data.reward_credits;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="refer-page">
      <div className="rounded-3xl p-7 md:p-10 bg-gradient-to-br from-sky to-sky-hover text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <Gift className="w-3.5 h-3.5"/> Refer & Earn
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold mt-3 leading-tight">
            Give a month.<br/>Get a month.
          </h1>
          <p className="opacity-90 mt-2 max-w-md">Invite a fellow pet parent — when they sign in, you both unlock 1 month of PetBond Premium (vet discounts + 1 free hostel night).</p>
        </div>
        <Sparkles className="absolute right-6 top-6 w-12 h-12 opacity-25" />
        <div className="absolute -right-10 -bottom-10 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
      </div>

      <div className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid="referral-code-card">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-ink-soft">Your invite code</div>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <div className="font-heading text-4xl md:text-5xl font-extrabold tracking-widest text-brand" data-testid="referral-code">{data.code}</div>
          <button onClick={copy} data-testid="copy-link-button" className="ml-auto bg-ink text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-2 hover:bg-brand transition-colors">
            {copied ? <><Check className="w-4 h-4"/> Copied</> : <><Copy className="w-4 h-4"/> Copy link</>}
          </button>
          <button onClick={share} data-testid="share-button" className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-2 hover:bg-brand-hover">
            <Share2 className="w-4 h-4"/> Share
          </button>
        </div>
        <div className="mt-4 bg-surface-muted rounded-2xl px-4 py-3 text-sm text-ink-soft break-all" data-testid="share-link">{shareUrl}</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="referrals-count-card">
          <div className="w-11 h-11 rounded-2xl bg-leaf grid place-items-center mb-3"><Users className="w-5 h-5 text-ink"/></div>
          <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">Friends joined</div>
          <div className="font-heading text-3xl font-extrabold mt-1">{data.referrals_count}</div>
        </div>
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="rewards-card">
          <div className="w-11 h-11 rounded-2xl bg-sunshine grid place-items-center mb-3"><Gift className="w-5 h-5 text-ink"/></div>
          <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">Premium months earned</div>
          <div className="font-heading text-3xl font-extrabold mt-1">{reward_months}</div>
        </div>
      </div>

      {!data.referred_by && (
        <form onSubmit={claim} className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="claim-form">
          <div className="font-heading text-xl font-extrabold">Have an invite code?</div>
          <p className="text-sm text-ink-soft mt-1">Enter it once to claim your free month.</p>
          <div className="mt-3 flex gap-2">
            <input
              data-testid="claim-code-input"
              value={claimCode}
              onChange={e => setClaimCode(e.target.value)}
              placeholder="ABCD1234"
              className="flex-1 bg-surface-muted border border-line rounded-full px-4 py-2.5 outline-none focus:border-brand font-bold tracking-widest uppercase"
              maxLength={12}
            />
            <button data-testid="claim-submit" className="bg-brand text-white rounded-full px-5 py-2.5 font-bold hover:bg-brand-hover">Claim</button>
          </div>
          {claimMsg && <div className="mt-3 text-sm font-bold text-leaf" data-testid="claim-message">{claimMsg}</div>}
        </form>
      )}

      <div className="text-xs text-ink-soft px-1">
        Rewards apply once per new account. Premium credit unlocks vet booking discount + 1 hostel night per month earned.
      </div>
    </div>
  );
}
